import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';

import {
  Printer,
  PrintJob,
  PrinterPagination,
  PrintJobFiltersPayload,
  ReprintJobResponse,
  PrintJobType
} from '../interfaces/printer.interface';

type LoadPrintJobsResponse = {
  print_jobs: PrintJob[];
  pagination: PrinterPagination;
};

@Injectable({
  providedIn: 'root'
})
export class PrinterService {
  private apiUrl = `printers`;

  constructor(private http: HttpClient) {}

  loadPrinters(): Observable<Printer[]> {
    return this.http
      .get<{
        printers: Printer[];
      }>(`${this.apiUrl}`)
      .pipe(map((res) => res.printers));
  }

  loadPrinterById(id: number): Observable<Printer> {
    return this.http
      .get<{
        printer: Printer;
      }>(`${this.apiUrl}/${id}`)
      .pipe(map((response) => response.printer));
  }

  loadPrintJobs(
    printerId: number,
    filters: PrintJobFiltersPayload = {}
  ): Observable<LoadPrintJobsResponse> {
    const params = new URLSearchParams();

    // Add all filter parameters to query string
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const queryString = params.toString();
    const url = `${this.apiUrl}/${printerId}/print_jobs${
      queryString ? `?${queryString}` : ''
    }`;

    // Log for debugging (can be removed in production)
    console.log('Print Jobs API Request:', {
      url,
      filters,
      queryString
    });

    return this.http.get<LoadPrintJobsResponse>(url);
  }

  reprintJob(printerId: number, jobId: number): Observable<ReprintJobResponse> {
    return this.http.post<ReprintJobResponse>(
      `${this.apiUrl}/${printerId}/print_jobs/${jobId}/reprint`,
      {}
    );
  }

  reprintBatch(printerId: number, batchId: number): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/${printerId}/batch_print_jobs/${batchId}/reprint`,
      {}
    );
  }

  refreshPrinterStatus(): Observable<Printer[]> {
    return this.http.post<Printer[]>(`${this.apiUrl}/refresh_status`, {});
  }

  getTypeText(type: PrintJobType): string {
    switch (type) {
      case 'PrintJob::OrderProductionTicket':
        return 'printers.ticket';
      case 'PrintJob::OpenCashDrawer':
        return 'printers.cashDrawer';
      case 'PrintJob::Receipt':
        return 'printers.receipt';
      case 'PrintJob::CashoutReport':
        return 'printers.cashoutReport';
      case 'PrintJob::CensusReport':
        return 'printers.censusReport';
      default:
        return type;
    }
  }
}
